import { ReactNode, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { Loader2 } from 'lucide-react';

interface CompanyOrGuestRouteProps {
  children: ReactNode;
  redirectTo?: string;
}

export function CompanyOrGuestRoute({ children, redirectTo = '/' }: CompanyOrGuestRouteProps) {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [hasAccess, setHasAccess] = useState(false);

  useEffect(() => {
    let isMounted = true;

    const checkAccess = async () => {
      try {
        const { data: { user }, error } = await supabase.auth.getUser();
        
        if (!isMounted) return;

        if (error || !user) {
          // Usuário não logado - permitir acesso como visitante
          setHasAccess(true);
          setLoading(false);
          return;
        }

        // Verificar se tem perfil de candidato no banco de dados
        const { data: candidateData } = await supabase
          .from('candidate_profiles')
          .select('id')
          .eq('user_id', user.id)
          .maybeSingle();

        if (!isMounted) return;

        // Obter contexto do localStorage
        const userContext = localStorage.getItem('userContext');

        if (candidateData && userContext === 'candidate') {
          // É candidato no contexto candidato - redirecionar
          navigate(redirectTo, { replace: true });
          return;
        }

        // Verificar se tem perfil de empresa
        const { data: companyData } = await supabase
          .from('company_profiles')
          .select('id')
          .eq('user_id', user.id)
          .maybeSingle();

        if (!isMounted) return;

        if (companyData) {
          // Tem perfil de empresa - permitir acesso
          setHasAccess(true);
          setLoading(false);
          return;
        }

        // Visitante sem perfil - permitir acesso
        setHasAccess(true);
        setLoading(false);
      } catch (error) {
        console.error('Error checking access:', error);
        if (isMounted) {
          setHasAccess(true);
          setLoading(false);
        }
      }
    };

    checkAccess();

    return () => {
      isMounted = false;
    };
  }, [navigate, redirectTo]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!hasAccess) {
    return null;
  }

  return <>{children}</>;
}
